import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import {
    useAssignPolicyToGroupsMutation,
    useGetPoliciesQuery,
    useUnassignPolicyFromGroupsMutation,
} from "@dashboard/policies/api";
import PolicyRulesTable from "@dashboard/shared/components/rules/PolicyRulesTable/PolicyRulesTable";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { selectPaneStatus, setGroupPolicy } from "@devices/store";
import { Group } from "@devices/types";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { withExpiredWrapper } from "@shared/ExpiredWrapper";
import { TextLink } from "@shared/TextLink";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

// Policy ids are never empty, so an empty value stands for "no policy".
const noPolicyValue = "";

function PolicyTab(group: Group): React.JSX.Element {
    const { t } = useTranslation(["sidepanels", "common"]);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { data: workspace = null } = useCurrentWorkspace();
    const { groupAbility, workspaceAbility } = PermissionService();
    const isSubscriptionExpired = useCurrentSubscriptionExpired();

    const ability = groupAbility(group, workspace);
    const cannotChangePolicy = ability.cannot("assign", "Policy") || ability.cannot("unassign", "Policy");
    const cannotUpdatePolicy = workspaceAbility(workspace).cannot("update", "Policy");
    const cannotCreatePolicy = workspaceAbility(workspace).cannot("create", "Policy");

    const {
        data: policiesResponse,
        isLoading: isLoadingPolicies,
        isError: isPoliciesError,
    } = useGetPoliciesQuery(workspaceId !== null && !isSubscriptionExpired ? { workspaceId } : skipToken);
    const policies = policiesResponse?.list ?? [];

    const [assignPolicyToGroups] = useAssignPolicyToGroupsMutation();
    const [unassignPolicyFromGroups] = useUnassignPolicyFromGroupsMutation();

    const policyOptions = [
        { label: t("sidepanels:groupPanel.policyTab.noPolicyOption"), value: noPolicyValue },
        ...policies.map((policy) => ({ label: policy.name, value: policy.id })).sort((a, b) => a.label.localeCompare(b.label)),
    ];

    const getDisabledTooltip = () => {
        if (isPoliciesError) {
            return t("common:error.policies.failedToGetPolicies");
        }
        return cannotChangePolicy ? t("common:tooltip.cannotManagePolicies") : undefined;
    };

    // The group changes in the store only once the backend accepted the change, so a failed one leaves nothing to undo.
    const handlePolicyChange = async (value: string | number | null) => {
        const selectedPolicyId = typeof value === "string" && value !== noPolicyValue ? value : null;
        if (selectedPolicyId === group.policyId) {
            return;
        }

        const result = selectedPolicyId
            ? await assignPolicyToGroups({ id: selectedPolicyId, groupIds: [group.id] })
            : await unassignPolicyFromGroups({ id: group.policyId ?? "", groupIds: [group.id] });
        if ("error" in result) {
            throw new Error(getErrorMessage(result.error));
        }

        const selectedPolicy = policies.find((policy) => policy.id === selectedPolicyId);
        dispatch(
            setGroupPolicy({
                groupId: group.id,
                policyId: selectedPolicy?.id ?? null,
                policyName: selectedPolicy?.name ?? null,
                policy: selectedPolicy?.content ?? null,
                policyHash: selectedPolicy?.hash ?? null,
            }),
        );
        showSuccessToast(
            selectedPolicyId
                ? t("sidepanels:groupPanel.policyTab.toast.policyAssigned")
                : t("sidepanels:groupPanel.policyTab.toast.policyUnassigned"),
        );
    };

    const getNoPolicyMessage = () => {
        if (isPoliciesError || cannotChangePolicy) {
            return t("sidepanels:groupPanel.policyTab.empty.noPolicy");
        }
        if (policies.length) {
            return t("sidepanels:groupPanel.policyTab.empty.choosePolicy");
        }
        if (cannotCreatePolicy) {
            return t("sidepanels:groupPanel.policyTab.empty.noPolicy");
        }
        return (
            <Trans
                t={t}
                i18nKey="sidepanels:groupPanel.policyTab.empty.createPolicy"
                components={{
                    // Not named "link": i18next parses <link> as a void element and would leave the label outside
                    // the anchor.
                    policiesLink: (
                        <TextLink
                            component="button"
                            type="button"
                            onClick={() => void navigate("/dashboard/policies")}
                            tooltip={t("sidepanels:groupPanel.policyTab.tooltip.goToPoliciesPage")}
                        />
                    ),
                }}
            />
        );
    };

    const paneStatus = useSelector((state) => selectPaneStatus(state));
    if (paneStatus === "pending" || isLoadingPolicies) {
        return <PaneElement.Loader />;
    }

    return (
        <PaneElement.Container>
            <PaneElement.Header>{t("sidepanels:groupPanel.policyTab.groupPolicy")}</PaneElement.Header>
            {isPoliciesError ? (
                <Box style={{ paddingBottom: 16 }}>
                    <CustomAlert severity="error">{t("common:error.policies.failedToGetPolicies")}</CustomAlert>
                </Box>
            ) : null}
            <Box style={{ paddingBottom: 16 }}>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:groupPanel.policyTab.assignedPolicy")}</PropertyKey>
                    <PropertyValue>
                        <PaneElement.EditableSelectInput
                            label={
                                group.policyId && group.policyName ? (
                                    <TextLink
                                        onClick={() => void navigate(`/dashboard/policies?policyId=${group.policyId}`)}
                                        tooltip={
                                            cannotUpdatePolicy
                                                ? t("sidepanels:groupPanel.policyTab.tooltip.showPolicy")
                                                : t("sidepanels:groupPanel.policyTab.tooltip.editPolicy")
                                        }
                                        truncatedTooltip={group.policyName}
                                    >
                                        {group.policyName}
                                    </TextLink>
                                ) : (
                                    "-"
                                )
                            }
                            initialValue={group.policyId ?? noPolicyValue}
                            onSave={handlePolicyChange}
                            options={policyOptions}
                            disabledTooltip={getDisabledTooltip()}
                            disableClearable
                            disableSorting
                        />
                    </PropertyValue>
                </PropertyRow>
            </Box>
            {group.policyId ? (
                <PolicyRulesTable content={group.policy ?? []} readOnly />
            ) : (
                <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minHeight: 0 }}>
                    <PaneElement.NoData message={getNoPolicyMessage()} />
                </Box>
            )}
        </PaneElement.Container>
    );
}
export default withExpiredWrapper(PolicyTab);
